/**
 * 시각 칸(`TimeField.tsx`)의 글자 다루기 — 24시간 `HH:MM`.
 *
 * 브라우저의 `<input type="time">` 은 사파리·웹킷에서 고를 목록이 없다. 칸마다 숫자를 쳐 넣어야 하고, 한국어
 * 로캘에서는 "오후 06:30" 으로 그려져 24시간으로 적는 운영 시간과 모양이 다르다. 그래서 글자 칸 + 시·분 목록으로
 * 바꿨고, 적은 글자는 여기서 읽는다.
 */

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * 적은 글자를 `HH:MM` 으로 — "7" → 07:00, "730" → 07:30, "1930" → 19:30, "7:5" → 07:05, "19.30" → 19:30,
 * "7시 30분" → 07:30. 24:00 은 00:00 이다(끝 칸에서는 자정까지 — 끝이 시작보다 이르면 다음 날이다).
 * 못 읽으면 `null` — 부르는 쪽이 앞의 값으로 되돌린다.
 */
export function parseTimeText(text: string): string | null {
  const compact = text.replace(/\s+/g, '');
  if (!compact) return null;
  let hour: number;
  let minute: number;
  const digits = /^\d{1,4}$/.exec(compact);
  if (digits) {
    const raw = digits[0];
    // 셋·넷이면 뒤 둘이 분이다(730 → 7:30). 하나·둘이면 시만.
    hour = Number(raw.length <= 2 ? raw : raw.slice(0, raw.length - 2));
    minute = raw.length <= 2 ? 0 : Number(raw.slice(-2));
  } else {
    const parts = /^(\d{1,2})(?:[:.시h])(\d{1,2})?분?$/i.exec(compact);
    if (!parts) return null;
    hour = Number(parts[1]);
    minute = parts[2] === undefined ? 0 : Number(parts[2]);
  }
  if (hour === 24 && minute === 0) return '00:00';
  if (hour > 23 || minute > 59) return null;
  return `${pad(hour)}:${pad(minute)}`;
}

/** `HH:MM` 을 분 단위로 옮긴다 — 하루를 돌아 넘는다(23:55 + 5분 = 00:00). 못 읽는 값은 그대로. */
export function stepTime(value: string, minutes: number): string {
  const parsed = /^(\d{2}):(\d{2})$/.exec(value);
  if (!parsed) return value;
  const total = (((Number(parsed[1]) * 60 + Number(parsed[2]) + minutes) % 1440) + 1440) % 1440;
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

export const HOUR_CHOICES: readonly string[] = Array.from({ length: 24 }, (_, hour) => pad(hour));

/** 분 목록 — 5분 간격. 지금 값이 그 사이(07:32)면 그 값도 넣는다 — 목록에 없으면 무엇이 골라져 있는지 안 보인다. */
export function minuteChoices(current: string): string[] {
  const choices = Array.from({ length: 12 }, (_, index) => pad(index * 5));
  const minute = /^\d{2}:(\d{2})$/.exec(current)?.[1];
  if (minute && !choices.includes(minute)) choices.push(minute);
  return choices.sort();
}
